import { apiRequest, ApiResponse } from '../request'
import type { StoreCollectionKind, ThemeContent, ThemeManifest, ThemeSectionInstance } from '@mediardex/contracts'

/** Tema na galeria. */
export interface StoreThemeSummary {
    slug: string
    name: string
    description: string
    segments: string[]
    /** Etiquetas de estilo (moda, casa...), de `THEME_STYLE_TAGS`. */
    styleTags: string[]
    version: number
    /** Amostra do preset que o card desenha: cores e fontes. */
    sample: {
        colorPrimary: string
        colorSecondary: string
        colorAccent: string
        colorBackground: string
        font: string
        headingFont: string
    }
}

/** Estado do editor (ver `StoreThemeService.getEditorState` no backend). */
export interface StoreThemeEditorState {
    store: { id: number; name: string; companyId: number }
    activeSlug: string
    themeSlug: string
    manifest: ThemeManifest
    content: ThemeContent
    hasDraft: boolean
    publishedAt: string | null
}

export interface StoreThemeVersion {
    id: number
    createdAt: string
    createdById: number | null
    manifestVersion: number
}

/** Loja na tela "Temas da Loja". */
export interface StoreThemeStore {
    id: number
    name: string
    subdomain: string
    active: boolean
    logo?: string | null
    themeSlug: string
    themeName: string
    colorPrimary: string | null
    hasDraft: boolean
    publishedAt: string | null
}

export interface StoreThemeFieldOptions {
    widgets: { id: number; name: string }[]
    categories: { id: number; name: string }[]
    collections: { id: number; name: string }[]
}

/** Lojas da empresa ativa, com o tema de cada uma. */
export async function listStoreThemeStores(): Promise<ApiResponse<StoreThemeStore[]>> {
    return apiRequest<StoreThemeStore[]>('store-themes/stores', {})
}

/** Widgets e categorias que os campos do editor oferecem. */
export async function getStoreThemeFieldOptions(
    storeConfigId: number,
): Promise<ApiResponse<StoreThemeFieldOptions>> {
    return apiRequest<StoreThemeFieldOptions>(`store-themes/store/${storeConfigId}/options`, {})
}

/** Envia imagem (até 10 MB) ou vídeo (até 50 MB) do tema e devolve a URL pública. */
export async function uploadStoreThemeMedia(
    storeConfigId: number,
    file: File,
): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData()
    formData.append('file', file)
    return apiRequest<{ url: string }>(
        `store-themes/store/${storeConfigId}/media`,
        { data: formData, formData: true },
        'post',
    )
}

export async function listStoreThemes(): Promise<ApiResponse<StoreThemeSummary[]>> {
    return apiRequest<StoreThemeSummary[]>('store-themes', {})
}

export async function getStoreThemeEditor(
    storeConfigId: number,
    themeSlug?: string,
): Promise<ApiResponse<StoreThemeEditorState>> {
    const query = themeSlug ? `?theme=${encodeURIComponent(themeSlug)}` : ''
    return apiRequest<StoreThemeEditorState>(`store-themes/store/${storeConfigId}${query}`, {})
}

/** Salva o rascunho. Não muda a vitrine. */
export async function saveStoreThemeDraft(
    storeConfigId: number,
    themeSlug: string,
    content: ThemeContent,
): Promise<ApiResponse<{ themeSlug: string; savedAt: string }>> {
    return apiRequest(`store-themes/store/${storeConfigId}/draft`, { data: { themeSlug, content } }, 'put')
}

export async function discardStoreThemeDraft(
    storeConfigId: number,
    themeSlug: string,
): Promise<ApiResponse<{ discarded: boolean }>> {
    return apiRequest(`store-themes/store/${storeConfigId}/draft/${themeSlug}`, {}, 'delete')
}

/** Publica o rascunho e deixa o tema ativo na loja. */
export async function publishStoreTheme(
    storeConfigId: number,
    themeSlug: string,
): Promise<ApiResponse<{ themeSlug: string; publishedAt: string }>> {
    return apiRequest(`store-themes/store/${storeConfigId}/publish`, { data: { themeSlug } }, 'post')
}

/**
 * Prepara a troca de tema: rascunho do tema novo com as configurações em comum. `catalog` é
 * o segmento escolhido na demonstração: o rascunho nasce com as fotos e os textos dele.
 */
export async function prepareStoreThemeSwitch(
    storeConfigId: number,
    themeSlug: string,
    catalog?: string,
): Promise<ApiResponse<StoreThemeEditorState>> {
    return apiRequest(`store-themes/store/${storeConfigId}/switch`, { data: { themeSlug, catalog } }, 'post')
}

/** Link da vitrine que desenha o rascunho (token de 2 horas). */
export async function createStoreThemePreview(
    storeConfigId: number,
    themeSlug: string,
): Promise<ApiResponse<{ token: string; url: string }>> {
    return apiRequest(`store-themes/store/${storeConfigId}/preview`, { data: { themeSlug } }, 'post')
}

/** Link da demonstração do tema: a vitrine com o catálogo demo e o tema de fábrica. */
export async function createStoreThemeDemo(
    storeConfigId: number,
    themeSlug: string,
    /** Segmento dos produtos da demo (etiqueta de `THEME_STYLE_TAGS`); vazio = o do tema. */
    catalog?: string,
): Promise<ApiResponse<{ token: string; url: string }>> {
    return apiRequest(`store-themes/store/${storeConfigId}/demo`, { data: { themeSlug, ...(catalog ? { catalog } : {}) } }, 'post')
}

export async function listStoreThemeVersions(
    storeConfigId: number,
    themeSlug: string,
): Promise<ApiResponse<StoreThemeVersion[]>> {
    return apiRequest<StoreThemeVersion[]>(`store-themes/store/${storeConfigId}/versions/${themeSlug}`, {})
}

/** Traz uma publicação anterior para o rascunho. */
export async function restoreStoreThemeVersion(
    storeConfigId: number,
    versionId: number,
): Promise<ApiResponse<{ themeSlug: string }>> {
    return apiRequest(`store-themes/store/${storeConfigId}/versions/${versionId}/restore`, {}, 'post')
}

// ---------------- Páginas institucionais ----------------

/** Página institucional no editor (`StorePage`). */
export interface StorePageItem {
    id: number
    slug: string
    title: string
    sections: ThemeSectionInstance[]
    draft: ThemeSectionInstance[] | null
    visible: boolean
    showInHeader: boolean
    showInSidebar: boolean
    showInFooter: boolean
    sortOrder: number
    seoTitle: string | null
    seoDescription: string | null
    seoImage: string | null
    publishedAt: string | null
}

export type StorePageMeta = Partial<
    Pick<
        StorePageItem,
        | 'title'
        | 'slug'
        | 'visible'
        | 'showInHeader'
        | 'showInSidebar'
        | 'showInFooter'
        | 'seoTitle'
        | 'seoDescription'
        | 'seoImage'
    >
>

export async function listStorePages(storeConfigId: number): Promise<ApiResponse<StorePageItem[]>> {
    return apiRequest<StorePageItem[]>(`store-themes/store/${storeConfigId}/pages`, {})
}

/** Página nova a partir de um modelo; entra na loja ao publicar. */
export async function createStorePage(
    storeConfigId: number,
    data: { themeSlug: string; templateKey: string; title?: string },
): Promise<ApiResponse<StorePageItem>> {
    return apiRequest<StorePageItem>(`store-themes/store/${storeConfigId}/pages`, { data }, 'post')
}

/** Título, endereço, visibilidade e SEO: valem na hora, sem esperar publicar. */
export async function updateStorePage(
    storeConfigId: number,
    pageId: number,
    data: StorePageMeta,
): Promise<ApiResponse<StorePageItem>> {
    return apiRequest<StorePageItem>(`store-themes/store/${storeConfigId}/pages/${pageId}`, { data }, 'patch')
}

export async function reorderStorePages(
    storeConfigId: number,
    pageIds: number[],
): Promise<ApiResponse<{ reordered: number }>> {
    return apiRequest(`store-themes/store/${storeConfigId}/pages/order`, { data: { pageIds } }, 'put')
}

export async function saveStorePageDraft(
    storeConfigId: number,
    pageId: number,
    themeSlug: string,
    sections: ThemeSectionInstance[],
): Promise<ApiResponse<{ pageId: number; savedAt: string }>> {
    return apiRequest(`store-themes/store/${storeConfigId}/pages/${pageId}/draft`, { data: { themeSlug, sections } }, 'put')
}

export async function removeStorePage(
    storeConfigId: number,
    pageId: number,
): Promise<ApiResponse<{ removed: boolean }>> {
    return apiRequest(`store-themes/store/${storeConfigId}/pages/${pageId}`, {}, 'delete')
}

// ---------------- Coleções ----------------

/** Coleção da loja no editor (`StoreCollection`). Vale na hora, sem publicar. */
export interface StoreCollectionItem {
    id: number
    slug: string
    title: string
    description: string | null
    image: string | null
    /** Selo de campanha na foto dos produtos (só na coleção manual). */
    badge: string | null
    kind: StoreCollectionKind
    categoryIds: number[]
    sort: string
    visible: boolean
    sortOrder: number
    seoTitle: string | null
    seoDescription: string | null
    seoImage: string | null
    /** Produtos da coleção manual, em ordem. */
    products: { catalogId: number }[]
}

export type StoreCollectionMeta = Partial<
    Pick<
        StoreCollectionItem,
        | 'title'
        | 'slug'
        | 'description'
        | 'image'
        | 'badge'
        | 'kind'
        | 'categoryIds'
        | 'sort'
        | 'visible'
        | 'seoTitle'
        | 'seoDescription'
        | 'seoImage'
    >
>

/** Produto da loja, para escolher os da coleção manual. */
export interface StoreThemeProduct {
    id: number
    name: string
    photo: string | null
    active: boolean
}

export async function listStoreCollections(storeConfigId: number): Promise<ApiResponse<StoreCollectionItem[]>> {
    return apiRequest<StoreCollectionItem[]>(`store-themes/store/${storeConfigId}/collections`, {})
}

export async function listStoreThemeProducts(storeConfigId: number): Promise<ApiResponse<StoreThemeProduct[]>> {
    return apiRequest<StoreThemeProduct[]>(`store-themes/store/${storeConfigId}/products`, {})
}

export async function createStoreCollection(
    storeConfigId: number,
    data: { title: string; kind: StoreCollectionKind; categoryIds?: number[] },
): Promise<ApiResponse<StoreCollectionItem>> {
    return apiRequest<StoreCollectionItem>(`store-themes/store/${storeConfigId}/collections`, { data }, 'post')
}

export async function updateStoreCollection(
    storeConfigId: number,
    collectionId: number,
    data: StoreCollectionMeta,
): Promise<ApiResponse<StoreCollectionItem>> {
    return apiRequest<StoreCollectionItem>(
        `store-themes/store/${storeConfigId}/collections/${collectionId}`,
        { data },
        'patch',
    )
}

/** Produtos da coleção manual, na ordem (substitui a lista). */
export async function setStoreCollectionProducts(
    storeConfigId: number,
    collectionId: number,
    catalogIds: number[],
): Promise<ApiResponse<StoreCollectionItem>> {
    return apiRequest<StoreCollectionItem>(
        `store-themes/store/${storeConfigId}/collections/${collectionId}/products`,
        { data: { catalogIds } },
        'put',
    )
}

export async function reorderStoreCollections(
    storeConfigId: number,
    collectionIds: number[],
): Promise<ApiResponse<{ reordered: number }>> {
    return apiRequest(`store-themes/store/${storeConfigId}/collections/order`, { data: { collectionIds } }, 'put')
}

export async function removeStoreCollection(
    storeConfigId: number,
    collectionId: number,
): Promise<ApiResponse<{ removed: boolean }>> {
    return apiRequest(`store-themes/store/${storeConfigId}/collections/${collectionId}`, {}, 'delete')
}
